import { IPaginator, PatientKind, SubscriptionFrequency } from "common/types";
import { IncentiveStatus } from "common/types/promotions";
import { RebateStatus } from "common/types/rebates";
import { parseNumber } from "common/utils/numbers";
import { toDollars, toPercent } from "common/utils/formatting";
import {
  convertStringPassthrough,
  pluralized,
  splitStringToValidValues,
} from "common/utils/strings";
import { Optional } from "common/utils/types";
import { isValueSet } from "common/utils";
import { toDate } from "common/utils/dates";

import { Paths } from "client/routes";

import {
  CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS,
  PRODUCT_FILTER_DEFINITIONS,
} from "../constants/filterDefinitions";
import {
  CarouselDisplayCarousel,
  CategoryCarousel,
  SelectedProductFilters,
  SelectedCategoryProductLineFilters,
  IProductItem,
  SubscriptionSelectItemProps,
  ProductSearchResult,
  ProductSearchDefaultItem,
  SelectedPromotionProductFilters,
  DiscountedPriceBreak,
  FilterDefinition,
  FilterValue,
  SearchFilterOption,
  ProductCardAddToCart,
  ProductCardBadge,
  PromotionDetails,
  PersonalizationKind,
} from "../types";

// `category === null` is the canonical Top Sellers carousel; remaining
// carousels render as category sections in the order returned by the BE.
export const partitionCarousels = (carousels: CarouselDisplayCarousel[]) => {
  const topSellerCarousel = carousels.find((c) => c.category === null);
  const categoryCarousels = carousels.filter((c): c is CategoryCarousel => c.category !== null);
  return { topSellerCarousel, categoryCarousels };
};

const parsedIds = (ids: Optional<number[] | string>) => {
  if (!ids) {
    return [];
  }
  if (ids instanceof Array) {
    return ids;
  }
  return (
    ids
      .split(",")
      .map((val) => parseNumber(val, 0))
      .filter((val) => !!val) ?? []
  );
};

const parsedBoolean = (bool: Optional<boolean[] | string>) => {
  if (!isValueSet(bool)) {
    return [];
  }
  if (bool instanceof Array) {
    return bool;
  }
  return bool.split(",").map((val) => val === "true");
};

export const getProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  incentive_ids,
  rebate_ids,
  prescription_required,
  patient_ids,
}: Pick<
  Paths["products"]["search"]["Search"],
  | "category_ids"
  | "manufacturer_ids"
  | "species"
  | "incentive_ids"
  | "rebate_ids"
  | "prescription_required"
  | "patient_ids"
>): SelectedProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    incentive_ids: parsedIds(incentive_ids),
    rebate_ids: parsedIds(rebate_ids),
    prescription_required: parsedBoolean(prescription_required),
    patient_ids: parsedIds(patient_ids),
  };
};

export const getRebateProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  prescription_required,
  patient_ids,
}: Paths["products"]["rebate"]["Search"]): SelectedPromotionProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
    patient_ids: parsedIds(patient_ids),
  };
};

export const getIncentiveProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  prescription_required,
  patient_ids,
}: Paths["products"]["incentive"]["Search"]): SelectedPromotionProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
    patient_ids: parsedIds(patient_ids),
  };
};

export const getCategoryProductLineFiltersFromSearch = ({
  manufacturer_ids,
  species,
  prescription_required,
  patient_ids,
}: Paths["products"]["category"]["Search"]): SelectedCategoryProductLineFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
    patient_ids: parsedIds(patient_ids),
  };
};

/** Keys no definition owns are left alone. */
export const clearedFilterValues = <TSearch, TValue extends FilterValue, TFilterOptions>(
  definitions: FilterDefinition<TSearch, TValue, TFilterOptions>[],
  current: TSearch,
): Partial<TSearch> =>
  definitions.reduce<Partial<TSearch>>(
    (cleared, definition) => ({
      ...cleared,
      ...definition.toUpdate([], { ...current, ...cleared }),
    }),
    {},
  );

/** Nothing selected, for a page's URL defaults. Pets are listed by hand: their definition is a hook. */
export const EMPTY_PRODUCT_SELECTION = {
  ...clearedFilterValues(PRODUCT_FILTER_DEFINITIONS, {} as SelectedProductFilters),
  patient_ids: [],
} as SelectedProductFilters;

export const EMPTY_CATEGORY_PRODUCT_LINE_SELECTION = {
  ...clearedFilterValues(
    CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS,
    {} as SelectedCategoryProductLineFilters,
  ),
  patient_ids: [],
} as SelectedCategoryProductLineFilters;

/**
 * Indents child categories under their parent, one level only. A child whose parent is missing
 * from the options is promoted to the top level rather than dropped.
 */
export const nestByParentCategory = (
  options: SearchFilterOption<FilterValue>[],
): { option: SearchFilterOption<FilterValue>; depth: number }[] => {
  const optionsByValue = new Map(options.map((option) => [option.value, option]));
  const isPrimaryCategory = (option: SearchFilterOption<FilterValue>) =>
    option.parent_id == null || !optionsByValue.has(option.parent_id);

  const primaryOptions = options.filter(isPrimaryCategory);
  const childOptions = options.filter((option) => !isPrimaryCategory(option));

  return primaryOptions.flatMap((primaryOption) => [
    { option: primaryOption, depth: 0 },
    ...childOptions
      .filter((childOption) => childOption.parent_id === primaryOption.value)
      .map((childOption) => ({ option: childOption, depth: 1 })),
  ]);
};

/**
 * Top-level options fill the limit first, then children backfill what is left. Ranking the two
 * together would let the largest subtree bury every other top-level option, since a parent's
 * count is the sum of its children's. A selection past the cut is added back with its parent.
 */
export const collapsedOptions = <TValue extends FilterValue>({
  options,
  selected,
  limit,
}: {
  options: SearchFilterOption<TValue>[];
  selected: TValue[];
  limit: number;
}): SearchFilterOption<TValue>[] => {
  const selectedValues = new Set<FilterValue>(selected);
  const optionsByValue = new Map<FilterValue, SearchFilterOption<TValue>>(
    options.map((option) => [option.value, option]),
  );
  const isTopLevel = (option: SearchFilterOption<TValue>) =>
    option.parent_id == null || !optionsByValue.has(option.parent_id);

  const optionsToShow = new Map<FilterValue, SearchFilterOption<TValue>>();
  const keepUntilLimitReached = (candidates: SearchFilterOption<TValue>[]) =>
    candidates.forEach((option) => {
      if (optionsToShow.size < limit) {
        optionsToShow.set(option.value, option);
      }
    });

  keepUntilLimitReached(options.filter(isTopLevel));
  keepUntilLimitReached(options);
  options
    .filter((option) => selectedValues.has(option.value))
    .forEach((option) => {
      optionsToShow.set(option.value, option);
      const parentOption =
        option.parent_id != null ? optionsByValue.get(option.parent_id) : undefined;
      if (parentOption) {
        optionsToShow.set(parentOption.value, parentOption);
      }
    });

  return options.filter((option) => optionsToShow.has(option.value));
};

/**
 * The filters the search response returned options for, even an empty list. A filter the
 * response leaves out is locked by the page or not offered to the clinic, so Reset leaves it alone.
 */
export const carriedDefinitions = <TSearch, TFilterOptions>(
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[],
  filterOptions: TFilterOptions | undefined,
) =>
  filterOptions === undefined
    ? []
    : definitions.filter((definition) => definition.optionsFrom(filterOptions) !== undefined);

/**
 * The filters the panel renders: those with options, minus any marked `isHiddenFromPanel`, and minus
 * empty ones unless the definition has an empty state to show.
 */
export const filtersWithOptions = <TSearch, TFilterOptions>({
  definitions,
  filterOptions,
}: {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  filterOptions: TFilterOptions;
}) =>
  definitions.flatMap((definition) => {
    const options = definition.optionsFrom(filterOptions);
    if (options === undefined || definition.isHiddenFromPanel) {
      return [];
    }
    return options.length || definition.emptyState ? [{ definition, options }] : [];
  });

/**
 * Selected values the response offers. A value with no option has no chip and, on the backend,
 * no effect, so it counts for nothing here either.
 */
export const countSelectedFilterValues = <TSearch, TFilterOptions>({
  definitions,
  filters,
  filterOptions,
}: {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  filters: TSearch;
  filterOptions: TFilterOptions | undefined;
}): number =>
  filterOptions === undefined
    ? 0
    : definitions.reduce((total, definition) => {
        const offered = new Set(
          (definition.optionsFrom(filterOptions) ?? []).map((option) => option.value),
        );
        return (
          total + definition.selectedFrom(filters).filter((value) => offered.has(value)).length
        );
      }, 0);

export const hasAnySelectedFilterValue = <TSearch, TFilterOptions>(options: {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  filters: TSearch;
  filterOptions: TFilterOptions | undefined;
}) => countSelectedFilterValues(options) > 0;

/** Each value a filter change selected or cleared, with its filter's values after the change. */
export const filterValueChanges = <TSearch, TFilterOptions>({
  definitions,
  before,
  after,
}: {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  before: TSearch;
  after: TSearch;
}) =>
  definitions.flatMap((definition) => {
    const previous = definition.selectedFrom(before);
    const next = definition.selectedFrom(after);
    const change = (value: FilterValue, selected: boolean) => ({
      filter: definition.key,
      value,
      selected,
      selected_values: next,
    });
    return [
      ...next.filter((value) => !previous.includes(value)).map((value) => change(value, true)),
      ...previous.filter((value) => !next.includes(value)).map((value) => change(value, false)),
    ];
  });

export const getSearchString = (paginator?: IPaginator, entityDescription: string = "result") => {
  const start = paginator?.page_start_index || 0;
  const end = paginator?.page_end_index || 0;
  const count = paginator?.count || 0;
  const entity = pluralized(count, entityDescription);
  return `Showing ${start} - ${end} of ${count} ${entity}`;
};

/* Discounted per-unit price at a given quantity, respecting vendor quantity tiers.

   Items with multiple price breaks are priced per tier; the discounted_price_breaks
   carry the rebate/subscription discount already applied per tier, so the selected
   quantity resolves to the correct discounted price. Items with a single (or no)
   tier fall back to the headline discounted fields. */
export const getItemPriceFromQuantity = (
  item: IProductItem | SubscriptionSelectItemProps,
  quantity: number,
  subscription: SubscriptionFrequency | null,
  discountedPriceBreaks: DiscountedPriceBreak[],
) => {
  if (discountedPriceBreaks.length <= 1) {
    return subscription
      ? item.subscription_discounted_price
      : item.non_subscription_discounted_price;
  }
  const relevantPriceBreak = discountedPriceBreaks.find(
    (priceBreak) => quantity >= priceBreak.low_qty && quantity <= priceBreak.high_qty,
  );
  if (relevantPriceBreak) {
    return subscription
      ? relevantPriceBreak.subscription_discounted_price
      : relevantPriceBreak.non_subscription_discounted_price;
  }
  return subscription ? item.subscription_discounted_price : item.non_subscription_discounted_price;
};

export const toPromotionDateRange = (
  start: string | null = null,
  end: string | null = null,
): string => {
  if (start && end) {
    return `Offer valid from ${toDate(start)} to ${toDate(end)}`;
  }
  if (!start && end) {
    return `Offer valid until ${toDate(end)}`;
  }
  if (start && !end) {
    return `Offer valid since ${toDate(start)}`;
  }
  return "Offer always valid. No expiration";
};

type PromoCodePunctuation = {
  [key: string]: string;
};

/**
 * Determines the proper punctuation and "or"s to be added between promo codes.
 *
 * @param codes The promo codes that the user can choose from
 * @returns A dictionary where key is promo code and value is string to prepend
 */
export const getPromoCodePunctuation = (codes: string[]) => {
  const numCodes = codes.length;
  const stringsToPrepend: PromoCodePunctuation = {};

  codes.forEach((code, index) => {
    if (index === 0) {
      stringsToPrepend[code] = "";
    } else if (index === numCodes - 1) {
      stringsToPrepend[code] = numCodes === 2 ? " or " : ", or ";
    } else {
      stringsToPrepend[code] = ", ";
    }
  });
  return stringsToPrepend;
};

/** The period to put after a promo message, unless it already ends in punctuation. */
export const getPromoCodeMessagePrefix = (promoMessage: string) =>
  [".", "!", "?"].includes(promoMessage.slice(-1)) ? "" : ".";

/**
 * Calculate dose price from unit price and units count.
 * Returns null if units_count is not available or zero, or if unitPrice is not a valid number.
 */
export const calculateDosePrice = (unitPrice: number, unitsCount: number | null): number | null => {
  if (!unitsCount || unitsCount <= 0 || !Number.isFinite(unitPrice)) {
    return null;
  }
  return Math.round(unitPrice / unitsCount);
};

/**
 * Check if dose pricing is available for an item.
 * Validates units_count and dose_label to ensure complete dose pricing display.
 */
export const hasDosePricing = (item: {
  units_count: number | null;
  dose_label: string | null;
}): boolean => {
  return item.units_count !== null && item.units_count > 0 && item.dose_label !== null;
};

/**
 * Whether a card's savings badges fit on the line the one-time price sits on. One plain badge does;
 * a promo code carries its own chip and a second badge needs the width, so those take the row
 * under the Autoship line instead, which keeps the two prices next to each other.
 */
export const canInlineBadgesOnMobileCard = (badges: ProductCardBadge[]) =>
  badges.length === 0 || (badges.length === 1 && !badges[0].promo_code);

/**
 * A savings badge's label. A capped percent promotion (max_savings set) can hit its dollar
 * ceiling, so the rate is only an upper bound.
 */
export const badgeLabel = (badge: ProductCardBadge): string => {
  if (badge.percent != null) {
    const rate = `${toPercent(String(badge.percent))} off`;
    return badge.max_savings == null ? rate : `Up to ${rate}`;
  }
  return `${toDollars(badge.savings_amount, { collapseWholeDollars: true })} off`;
};

// Drop empty/missing segments so the "·" separators don't double up.
export const getVariantLabel = ({
  primary_label,
  secondary_label,
}: Pick<ProductSearchResult, "primary_label" | "secondary_label">) =>
  [primary_label, secondary_label].filter(Boolean).join(" · ");

export const getSubscriptionPrice = (
  defaultItem: Pick<ProductSearchDefaultItem, "subscription_price" | "standard_price">,
) => defaultItem.subscription_price ?? defaultItem.standard_price;

type AddToCartPricingParams = {
  oneTimePrice: number;
  subscriptionPrice: number;
  isSubscription: boolean;
};

/** The unit price an add-to-cart event reports, and its saving over the one-time price. */
export const getAddToCartPricing = ({
  oneTimePrice,
  subscriptionPrice,
  isSubscription,
}: AddToCartPricingParams) =>
  isSubscription
    ? { price: subscriptionPrice, discount: oneTimePrice - subscriptionPrice }
    : { price: oneTimePrice, discount: 0 };

type AddToCartLabelParams = Pick<ProductCardAddToCart, "is_available" | "quantity">;

export const getAddToCartLabel = ({ is_available, quantity }: AddToCartLabelParams) => {
  if (!is_available) {
    return "Unavailable";
  }
  return quantity === 1 ? "Add to cart" : `Add ${quantity} to cart`;
};

type PromotionPageStatus = "live" | "expired" | "inactive";

export const getPromotionPageStatus = (
  promotion: Pick<PromotionDetails, "status">,
  isStaff: boolean,
): PromotionPageStatus => {
  switch (promotion.status) {
    case IncentiveStatus.Live:
    case RebateStatus.Active:
      return "live";
    case RebateStatus.Previewing:
      return isStaff ? "live" : "inactive";
    case IncentiveStatus.Expired:
    case RebateStatus.Expired:
      return "expired";
    default:
      return "inactive";
  }
};

export const getAddToCartAndSubscribeLabel = (params: AddToCartLabelParams) => {
  const label = getAddToCartLabel(params);
  return params.is_available ? `${label} & subscribe` : label;
};

type SubscriptionSavingsTextParams = {
  oneTimePrice: number;
  subscriptionPrice: number;
  isAddingMultipleUnits: boolean;
  isSubscriptionSelected: boolean;
};

export const getSubscriptionSavingsText = ({
  oneTimePrice,
  subscriptionPrice,
  isAddingMultipleUnits,
  isSubscriptionSelected,
}: SubscriptionSavingsTextParams) => {
  const savings = `${toDollars(oneTimePrice - subscriptionPrice)}${isAddingMultipleUnits ? " each" : ""}`;
  return isSubscriptionSelected
    ? `Save ${savings} with Autoship`
    : `Switch to Autoship to save ${savings}`;
};

type ProductLineCardChipSource = {
  patient_prescription_label: Optional<string>;
  personalization_kind?: Optional<PersonalizationKind>;
  is_featured: boolean;
  is_loyalty_program_featured?: boolean;
};

export type ProductLineCardChip = {
  variant: "info" | "accent-fill" | "grey-fill" | "highlight";
  label: string;
};

const personalizationKindToChip: Record<
  Exclude<PersonalizationKind, PersonalizationKind.Prescribed>,
  ProductLineCardChip
> = {
  [PersonalizationKind.RecentlyPurchased]: { variant: "accent-fill", label: "Recently Purchased" },
  [PersonalizationKind.RecentlyViewed]: { variant: "grey-fill", label: "Recently Viewed" },
};

/** Precedence: prescribed, recently purchased, recently viewed, then Your Vet's Choice. */
export const getProductLineCardChip = (
  productLine: ProductLineCardChipSource,
): ProductLineCardChip | null => {
  if (productLine.patient_prescription_label) {
    return { variant: "info", label: productLine.patient_prescription_label };
  }
  if (
    productLine.personalization_kind &&
    productLine.personalization_kind !== PersonalizationKind.Prescribed
  ) {
    return personalizationKindToChip[productLine.personalization_kind];
  }
  if (productLine.is_loyalty_program_featured || productLine.is_featured) {
    return { variant: "highlight", label: "Your Vet's Choice" };
  }
  return null;
};
